import { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

function api(path, token, options = {}) {
  return fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    ...options,
  }).then((r) => r.json());
}

export default function UserManagement({ token }) {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Ticket adjustment
  const [ticketType, setTicketType] = useState('DIGITAL');
  const [ticketCount, setTicketCount] = useState(1);
  const [adjusting, setAdjusting] = useState(false);

  const searchUsers = useCallback(async () => {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const data = await api(`/admin/users/search?query=${encodeURIComponent(search.trim())}`, token);
      setUsers(data.users || []);
      setFetched(true);
      setSelectedUser(null);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [search, token]);

  const handleSearch = (e) => {
    e.preventDefault();
    searchUsers();
  };

  const handleAdjust = async (direction) => {
    if (!selectedUser) return;
    const count = direction === 'subtract' ? -ticketCount : ticketCount;
    const action = direction === 'subtract' ? '차감' : '추가';

    if (!window.confirm(`${selectedUser.name || selectedUser.tossUserId}님의 ${ticketType === 'DIGITAL' ? '디지털' : '실물'} 발송권을 ${Math.abs(count)}장 ${action}하시겠습니까?`)) return;

    setAdjusting(true);
    try {
      const res = await api('/admin/tickets/grant', token, {
        method: 'POST',
        body: JSON.stringify({ tossUserId: selectedUser.tossUserId, type: ticketType, count }),
      });
      if (res.error) {
        alert(`실패: ${res.error}`);
      } else {
        alert(`${action} 완료`);
        // Refresh user list
        await searchUsers();
      }
    } catch (e) {
      alert('처리 중 오류가 발생했습니다.');
    }
    setAdjusting(false);
  };

  // Detail view
  if (selectedUser) {
    return (
      <div className="payments-page">
        <button className="back-btn" onClick={() => setSelectedUser(null)}>
          &larr; 목록으로
        </button>

        <div className="detail-container">
          <div className="detail-card">
            <h3>사용자 정보</h3>
            <div className="detail-grid">
              <div className="detail-row"><label>이름</label><span>{selectedUser.name || '-'}</span></div>
              <div className="detail-row"><label>사용자 ID</label><span style={{ fontSize: 12, wordBreak: 'break-all' }}>{selectedUser.tossUserId}</span></div>
              <div className="detail-row"><label>연락처</label><span>{selectedUser.phone || '-'}</span></div>
              <div className="detail-row"><label>이메일</label><span>{selectedUser.email || '-'}</span></div>
              <div className="detail-row"><label>가입일</label><span>{selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString('ko-KR') : '-'}</span></div>
            </div>
          </div>

          <div className="detail-card">
            <h3>발송권 현황</h3>
            <div className="detail-grid">
              <div className="detail-row"><label>디지털 발송권</label><span className="ticket-count">{selectedUser.digitalTickets}장</span></div>
              <div className="detail-row"><label>실물 발송권</label><span className="ticket-count">{selectedUser.physicalTickets}장</span></div>
            </div>
          </div>

          <div className="detail-card">
            <h3>발송권 조절</h3>
            <div className="ticket-adjust">
              <div className="ticket-adjust-row">
                <label>티켓 종류</label>
                <select value={ticketType} onChange={(e) => setTicketType(e.target.value)} className="ticket-select">
                  <option value="DIGITAL">디지털</option>
                  <option value="PHYSICAL">실물</option>
                </select>
              </div>
              <div className="ticket-adjust-row">
                <label>수량</label>
                <div className="ticket-counter">
                  <button onClick={() => setTicketCount(Math.max(1, ticketCount - 1))} className="counter-btn">-</button>
                  <input
                    type="number"
                    value={ticketCount}
                    onChange={(e) => setTicketCount(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
                    className="counter-input"
                    min="1"
                    max="200"
                  />
                  <button onClick={() => setTicketCount(Math.min(200, ticketCount + 1))} className="counter-btn">+</button>
                </div>
              </div>
              <div className="ticket-adjust-actions">
                <button
                  className="ticket-add-btn"
                  onClick={() => handleAdjust('add')}
                  disabled={adjusting}
                >
                  {adjusting ? '처리 중...' : `${ticketCount}장 추가`}
                </button>
                <button
                  className="ticket-subtract-btn"
                  onClick={() => handleAdjust('subtract')}
                  disabled={adjusting}
                >
                  {adjusting ? '처리 중...' : `${ticketCount}장 차감`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="payments-page">
      <form className="search-bar" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="핸드폰 번호 또는 이메일로 검색..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" disabled={loading}>검색</button>
        {search && (
          <button type="button" onClick={() => { setSearch(''); setUsers([]); setFetched(false); }}>
            초기화
          </button>
        )}
      </form>

      {!fetched && !loading && (
        <div className="empty-state">
          <p>핸드폰 번호 또는 이메일로 사용자를 검색하세요.</p>
        </div>
      )}

      {loading && <p style={{ padding: 24 }}>검색 중...</p>}

      {!loading && users.length > 0 && (
        <div className="payment-table-wrap">
          <table className="payment-table">
            <thead>
              <tr>
                <th>이름</th>
                <th>연락처</th>
                <th>이메일</th>
                <th>디지털 발송권</th>
                <th>실물 발송권</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.tossUserId} onClick={() => setSelectedUser(u)} className="clickable-row">
                  <td>{u.name || '-'}</td>
                  <td>{u.phone || '-'}</td>
                  <td>{u.email || '-'}</td>
                  <td className="ticket-count">{u.digitalTickets}장</td>
                  <td className="ticket-count">{u.physicalTickets}장</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {fetched && !loading && users.length === 0 && (
        <div className="empty-state">
          <p>검색 결과가 없습니다.</p>
        </div>
      )}
    </div>
  );
}
